import { useEffect, useMemo, useState } from 'react';
import { adminApi, API } from '../lib/api';
import { Download, Eye, FileText, Trash2, X, Phone, Mail } from 'lucide-react';
import { CATEGORIES } from '../data/categories';

const STATUS_OPTS = [
  { value: 'new', label: 'Ny' },
  { value: 'sent', label: 'Sendt' },
  { value: 'converted', label: 'Konvertert' },
  { value: 'invalid', label: 'Ugyldig' },
];

const TIMEFRAME_BADGE = {
  'Snarest mulig':            { label: 'Snarest', cls: 'bg-red-100 text-red-700 ring-1 ring-red-200' },
  'Innen 1 uke':              { label: '1 uke',   cls: 'bg-orange-100 text-orange-700 ring-1 ring-orange-200' },
  'Innen 14 dager':           { label: '14 d.',   cls: 'bg-orange-100 text-orange-700 ring-1 ring-orange-200' },
  'Innen 1 måned':            { label: '1 mnd',   cls: 'bg-orange-100 text-orange-700 ring-1 ring-orange-200' },
  'Innen 3 måneder':          { label: '3 mnd',   cls: 'bg-blue-100 text-blue-700 ring-1 ring-blue-200' },
  'Bare å undersøke prisene': { label: 'Undersøker', cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200' },
};

const TimeframeBadge = ({ value }) => {
  if (!value) return <span className="text-[#64748B] text-xs">—</span>;
  const t = TIMEFRAME_BADGE[value] || { label: value, cls: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${t.cls}`}>
      {t.label}
    </span>
  );
};

const formatSource = (l) => {
  const src = l.utm_source;
  const camp = l.utm_campaign;
  if (src && camp) return `${src} / ${camp}`;
  if (src) return src;
  if (camp) return camp;
  if (l.gclid) return 'google-ads';
  if (l.fbclid) return 'meta-ads';
  if (l.referrer) {
    try { return new URL(l.referrer).hostname.replace(/^www\./, ''); } catch { /* ignore */ }
  }
  return 'direct';
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [status, setStatus] = useState('');
  const [timeframe, setTimeframe] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (cat) params.category = cat;
    if (status) params.status = status;
    try {
      const r = await adminApi.get('/admin/leads', { params });
      setLeads(r.data.leads || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Client-side filter for timeframe so we don't need a backend roundtrip.
  const filteredLeads = useMemo(
    () => (timeframe ? leads.filter((l) => l.timeframe === timeframe) : leads),
    [leads, timeframe],
  );

  // Quick stats — surface "urgent" lead count above the table.
  const urgentCount = useMemo(
    () => leads.filter((l) => ['Snarest mulig', 'Innen 1 uke', 'Innen 14 dager'].includes(l.timeframe || '')).length,
    [leads],
  );
  const newCount = useMemo(() => leads.filter((l) => l.status === 'new').length, [leads]);

  const updateStatus = async (id, newStatus) => {
    await adminApi.patch(`/admin/leads/${id}`, { status: newStatus });
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne henvendelsen?')) return;
    await adminApi.delete(`/admin/leads/${id}`);
    load();
  };

  const exportCsv = async () => {
    window.open(`${API}/admin/export`, '_blank');
  };

  const downloadPdf = (id) => {
    window.open(`${API}/admin/leads/${id}/pdf`, '_blank');
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#0A1F44]">Henvendelser</h1>
          <p className="text-[#64748B] text-sm mt-1">
            {leads.length} henvendelser
            {newCount > 0 && <span className="ml-2 inline-flex items-center gap-1 text-[#0A1F44] font-semibold"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />{newCount} nye</span>}
            {urgentCount > 0 && <span className="ml-2 inline-flex items-center gap-1 text-red-600 font-semibold"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />{urgentCount} haster</span>}
          </p>
        </div>
        <button onClick={exportCsv} data-testid="export-csv" className="bg-[#0A1F44] text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
          <Download size={14} /> Eksporter CSV
        </button>
      </header>

      {/* Quick prioritisation chips */}
      <div className="flex flex-wrap gap-2 -mt-2">
        {['', 'Snarest mulig', 'Innen 1 uke', 'Innen 1 måned', 'Innen 3 måneder', 'Bare å undersøke prisene'].map((tf) => (
          <button
            key={tf || 'all'}
            onClick={() => setTimeframe(tf)}
            data-testid={`tf-chip-${tf || 'alle'}`}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${timeframe === tf ? 'bg-[#0A1F44] text-white' : 'bg-white text-[#0A1F44] border border-[#0A1F44]/15 hover:border-[#0A1F44]/40'}`}
          >
            {tf || 'Alle frister'}{tf && ` (${leads.filter((l) => l.timeframe === tf).length})`}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl p-4 border border-[#0A1F44]/10 flex flex-wrap gap-3">
        <input data-testid="search-leads" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk navn, e-post, telefon…" className="bg-[#FAF8F5] border-0 rounded-lg px-4 py-2 text-sm flex-1 min-w-[200px]" />
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="bg-[#FAF8F5] border-0 rounded-lg px-4 py-2 text-sm">
          <option value="">Alle kategorier</option>
          {CATEGORIES.map((c) => (<option key={c.slug} value={c.name}>{c.name}</option>))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#FAF8F5] border-0 rounded-lg px-4 py-2 text-sm">
          <option value="">Alle statuser</option>
          {STATUS_OPTS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
        </select>
        <button onClick={load} data-testid="filter-apply" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-4 py-2 rounded-lg text-sm font-semibold">Filtrer</button>
      </div>

      <div className="bg-white rounded-xl border border-[#0A1F44]/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#64748B] bg-[#FAF8F5]">
                <th className="px-5 py-3">Tid</th><th>Navn</th><th>Telefon</th><th>Behandling</th><th>Klinikk</th><th>By</th><th>Frist</th><th>Kilde</th><th>Status</th><th className="pr-5 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-5 py-6 text-center text-[#64748B]">Laster…</td></tr>}
              {!loading && filteredLeads.length === 0 && <tr><td colSpan={10} className="px-5 py-6 text-center text-[#64748B]">Ingen henvendelser</td></tr>}
              {filteredLeads.map((l) => (
                <tr key={l.id} className="border-t border-[#0A1F44]/5 hover:bg-[#FAF8F5]" data-testid={`lead-row-${l.id.slice(0, 8)}`}>
                  <td className="px-5 py-3 text-[#64748B] whitespace-nowrap">{new Date(l.created_at).toLocaleString('no-NO')}</td>
                  <td className="font-semibold text-[#0A1F44]">{l.name}</td>
                  <td>
                    <a href={`tel:${l.phone}`} className="hover:text-[#3B82F6] inline-flex items-center gap-1" title="Ring">
                      <Phone size={11} className="opacity-60" /> {l.phone}
                    </a>
                  </td>
                  <td>{l.treatment}</td>
                  <td>{l.clinic_selected || '—'}</td>
                  <td>{l.city || '—'}</td>
                  <td data-testid={`lead-timeframe-${l.id.slice(0,8)}`}><TimeframeBadge value={l.timeframe} /></td>
                  <td data-testid={`lead-source-${l.id.slice(0,8)}`}>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#3B82F6]/10 text-[#0A1F44] font-medium" title={l.landing_page || ''}>
                      {formatSource(l)}
                    </span>
                  </td>
                  <td>
                    <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="bg-[#FAF8F5] border-0 rounded px-2 py-1 text-xs">
                      {STATUS_OPTS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                    </select>
                  </td>
                  <td className="pr-5 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setSelected(l)} title="Vis" className="p-1.5 hover:bg-[#0A1F44]/10 rounded" data-testid={`lead-view-${l.id.slice(0,8)}`}><Eye size={15} /></button>
                      <button onClick={() => downloadPdf(l.id)} title="PDF" className="p-1.5 hover:bg-[#0A1F44]/10 rounded"><FileText size={15} /></button>
                      <button onClick={() => del(l.id)} title="Slett" className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl p-7 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-display text-2xl font-bold text-[#0A1F44]">{selected.name}</h3>
                <p className="text-sm text-[#64748B]">{new Date(selected.created_at).toLocaleString('no-NO')}</p>
              </div>
              <button onClick={() => setSelected(null)}><X /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">Telefon</div><div className="text-[#0A1F44] font-medium">{selected.phone}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">E-post</div><div className="text-[#0A1F44] font-medium">{selected.email}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">By</div><div className="text-[#0A1F44] font-medium">{selected.city || '—'}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">Kategori</div><div className="text-[#0A1F44] font-medium">{selected.category}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">Behandling</div><div className="text-[#0A1F44] font-medium">{selected.treatment}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">Valgt klinikk</div><div className="text-[#0A1F44] font-medium">{selected.clinic_selected || '—'}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">Kilde</div><div className="text-[#0A1F44] font-medium">{formatSource(selected)}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">Landing</div><div className="text-[#0A1F44] font-medium text-xs break-all">{selected.landing_page || '—'}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">Kampanje</div><div className="text-[#0A1F44] font-medium">{selected.utm_campaign || '—'}</div></div>
            </div>
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">Melding</div>
              <div className="text-[#334155] bg-[#FAF8F5] rounded-lg p-4 text-sm">{selected.message || 'Ingen melding oppgitt'}</div>
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={() => downloadPdf(selected.id)} className="bg-[#0A1F44] text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><FileText size={14} /> Last ned PDF</button>
              <button onClick={() => setSelected(null)} className="bg-[#FAF8F5] text-[#0A1F44] px-4 py-2 rounded-lg text-sm">Lukk</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
